// Single source of truth for how a grass-ID confidence score (0-100) is
// presented across the web app: tier, label, bar/label colors, and the two
// treatment predicates the result cards branch on.
//
// Why this exists: the same threshold ladder used to be copy-pasted in
// `components/home/confidence-meter.tsx`, `components/home/results-display.tsx`,
// and `app/result/[id]/page-content.tsx`, and had already started to drift.
// Calibration lives here so a tweak lands on every surface at once.
//
// Calibration intent (measured/honest, 2026-06-23): the vision model now scores
// on what a phone photo can actually show (see the identify prompts), so a clear
// photo lands ~80-90%. The display tiers below treat the solid-match band as a
// confident success rather than a warning. The cutoffs line up with the
// `MIN_IDENTIFY_CONFIDENCE_TO_PERSIST = 50` junk gate, but persistence and
// presentation are deliberately different decisions. A savable 50-69 result
// remains a possible match; confirmation language starts at 70.

// Mobile mirror: mobile/lib/confidence-display.ts. Its tone ladder (70/50)
// matches these tiers; its CONFETTI_THRESHOLD (0.8) is intentionally NOT the
// same as the 85 'very-high' cutoff — it's a celebration gesture, not a tier.
export type ConfidenceTier = 'very-high' | 'high' | 'fair' | 'low'

/** Numeric tier for a 0-100 confidence score. */
export function getConfidenceTier(confidence: number): ConfidenceTier {
  if (confidence >= 85) return 'very-high'
  if (confidence >= 70) return 'high'
  if (confidence >= 50) return 'fair'
  return 'low'
}

/** Human label. The 50-69 band is useful without claiming confirmation. */
export function getConfidenceLabel(confidence: number): string {
  switch (getConfidenceTier(confidence)) {
    case 'very-high':
      return 'Very High Confidence'
    case 'high':
      return 'High Confidence'
    case 'fair':
      return 'Possible Match'
    default:
      return 'Best Regional Guess'
  }
}

/** Progress-bar fill color. Green is reserved for confirmed-looking tiers. */
export function getConfidenceBarColor(confidence: number): string {
  switch (getConfidenceTier(confidence)) {
    case 'very-high':
    case 'high':
      return 'bg-green-500'
    case 'fair':
      return 'bg-sky-500'
    default:
      return 'bg-gray-400'
  }
}

/** Label text color, paired with `getConfidenceBarColor`. */
export function getConfidenceLabelColor(confidence: number): string {
  switch (getConfidenceTier(confidence)) {
    case 'very-high':
    case 'high':
      return 'text-green-700'
    case 'fair':
      return 'text-sky-700'
    default:
      return 'text-gray-600'
  }
}

/**
 * Confident enough to lead with confirmation language and the strong-match
 * layout. This must stay separate from the 50+ persistence and care-plan gate.
 */
export function isHighConfidence(confidence: number): boolean {
  return confidence >= 70
}

/**
 * Genuinely low — show the honest "regional best guess" nudge + improve-photo
 * prompt. Below 50 (which is also below the persist junk gate), so in practice
 * this is the live-but-unsaved borderline plus the parse-failure buckets.
 */
export function isLowConfidence(confidence: number): boolean {
  return confidence < 50
}

/**
 * The uncalibrated-score caveat. Every surface that prints a model score reads
 * this one string so the meter, the hero and the ranked list cannot drift into
 * three different promises. It qualifies the SCORE only; it says nothing about
 * a user-selected identity, which carries no model score at all.
 */
export const CONFIDENCE_SCORE_CAVEAT = 'A match score from your photo, not a guarantee.'
